import { useCallback, useEffect, useRef } from 'react';
import { ApiError } from '../lib/api';
import { ensureAnonId, forgetAnonId, postView } from '../queries/share';

/**
 * The view beacon.
 *
 * One row per viewer per video, updated in place, so this can fire often
 * without inflating anything. It reports the furthest point reached, which is
 * what makes "watched 80% of it" a real number on the dashboard rather than a
 * count of people who opened a link and left.
 *
 * Fires: once on first play, every 10 seconds of playback, on pause, and when
 * the tab goes away. Never while paused, and never twice for the same second.
 */

const INTERVAL_MS = 10_000;
const COMPLETION_RATIO = 0.95;

export function useViewBeacon(shareId: string, enabled: boolean) {
  const lastSentRef = useRef(-1);
  const progressRef = useRef(0);
  const durationRef = useRef(0);
  const completedRef = useRef(false);
  const inFlightRef = useRef(false);

  const send = useCallback(
    (force: boolean) => {
      if (!enabled || inFlightRef.current) return;

      const progress = Math.floor(progressRef.current);
      const duration = durationRef.current;
      const completed =
        duration > 0 && progress >= duration * COMPLETION_RATIO ? true : completedRef.current;

      if (!force && progress === lastSentRef.current && completed === completedRef.current) return;

      lastSentRef.current = progress;
      completedRef.current = completed;
      inFlightRef.current = true;

      void ensureAnonId()
        .then((anonId) => postView(shareId, { anonId, progressSec: progress, completed }))
        .catch((error: unknown) => {
          // An id the API no longer recognises is dropped, and the next
          // beacon mints a fresh one. Anything else: a view that does not
          // record is not worth telling anybody about.
          if (error instanceof ApiError && error.code === 'anon_id_required') forgetAnonId();
        })
        .finally(() => {
          inFlightRef.current = false;
        });
    },
    [enabled, shareId],
  );

  /** Call from the player's progress handler. */
  const report = useCallback((currentTime: number, duration: number) => {
    progressRef.current = Math.max(progressRef.current, currentTime);
    if (Number.isFinite(duration) && duration > 0) durationRef.current = duration;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // The open itself counts as a view, even if they never press play.
    send(true);
    const timer = window.setInterval(() => send(false), INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') send(true);
    };
    const onPageHide = () => send(true);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      send(true);
    };
  }, [enabled, send]);

  return { report, flush: () => send(true) };
}
